<!--
	Form表单组件
-->
<template>
  <ElForm
    ref="formRef"
    v-bind="baseAttrs"
    :model="vModel"
    :disabled="disabled"
    :validate-on-rule-change="false"
    :rules="rules"
    class="fm-cont"
  >
    <slot />
  </ElForm>
</template>

<script lang="ts">
/**
 * ### form表单容器组件
 */
import { defineComponent, PropType, ref, reactive, toRaw } from 'vue';
import { ElForm, FormInstance, ElLoading, FormItemRule } from 'element-plus';
import { shareData, protectedReactive } from '../core/SharedData';
import { axiosRequest, axiosPost, showError } from '../core/Common';
import { PlainObject } from '../core/types';
import { IgnoreError, TipError } from '../core/Errors';
import { usePopupContainer } from '../common/popup/PopupContainer';
import { FormObjectId, FormSubmitParams } from './types';
import { ValidateFieldsError } from 'async-validator';
import _ from 'lodash-es';

declare type Arrayable<T> = T | T[];

export default defineComponent({
  extends: ElForm,
  props: {
    /**
     * ### 表单标识
     * <warning> 与本地存储关联 在所有页面中需唯一
     */
    name: { type: String, required: true },
    /**
     * ### form表单的v-model对象 如若不提供则组件自己生成一个
     */
    model: { type: Object as PropType<PlainObject>, default: undefined },
    /**
     * ### 表单加载时获取初始数据的地址
     * <warning> 需设置objectId
     */
    fetchUrl: { type: String, default: undefined },
    /**
     * ### 新增记录url 如若未指定则使用saveUrl
     */
    createUrl: { type: String, default: undefined },
    /**
     * ### 更新记录url
     */
    saveUrl: { type: String, default: undefined },
    /**
     * ### 更新记录的ID
     * 如若提供 isValid 方法则根据 isValid方法返回值确定是新增还是更新
     * 否则提供非false值则为更新
     */
    objectId: { type: Object as PropType<FormObjectId>, default: undefined },
    /**
     * ### fetchUrl 的http method
     */
    fetchMethod: { type: String as PropType<'post' | 'get'>, default: 'post' },
    /**
     * ### 新增时提交的数据外加的一层json封装key 如若未指定则使用saveWrapKey
     */
    createWrapKey: { type: String, default: undefined },
    /**
     * ### 更新时提交的数据外加的一层json封装key
     */
    saveWrapKey: { type: String, default: undefined },
    /**
     * ### 行内表单模式
     */
    inline: { type: Boolean, default: true },
    /**
     * ### 标签的长度
     */
    labelWidth: { type: [String, Number], default: '80px' },
    /**
     * ### 是否禁用
     */
    disabled: { type: Boolean, default: false },
    /**
     * ### 表单校验未通过时的回调函数
     */
    invalidFields: {
      type: Function as PropType<(fields: ValidateFieldsError) => void>,
      default: undefined
    },
    /**
     * ### 表单提交异常回调
     */
    submitCatch: {
      type: Function as PropType<(data: PlainObject) => void>,
      default: undefined
    }
  },

  emits: {
    loaded: null
  },

  setup(props, ctx) {
    const formRef = ref<FormInstance>();

    const baseAttrs = _.omit(props, [
      'name',
      'model',
      'fetchUrl',
      'fetchMethod',
      'saveUrl',
      'params',
      'rules',
      'disabled',
      'objectId',
      'popupCtx'
    ]) as any;

    // 校验规则
    const rules = ref<Record<string, Arrayable<FormItemRule>>>();

    // 除表格外其它输入字段
    const formItems: Record<string, { persist?: () => void } | boolean> =
      protectedReactive();

    // 各表格的提交的参数
    const tableInputs: Record<string, () => void> = protectedReactive();

    // 是不是编辑，还是新建
    const hasObjectId = props.objectId?.isValid
      ? props.objectId.isValid()
      : !!props.objectId;

    // params为空表明是新建，否则为update
    const saveUrl = hasObjectId
      ? props.saveUrl
      : props.createUrl ?? props.saveUrl;

    // post时增加时外加一层json封装, 这外为json key
    const wrapKey = hasObjectId
      ? props.saveWrapKey
      : props.createWrapKey ?? props.saveWrapKey;

    // 如果没有传model, 构造一个
    const model = props.model ?? reactive<PlainObject>({});
    const loading = ref(false); // save loading
    const submit = async (hook?: FormSubmitParams) => {
      if (loading.value) {
        throw new TipError('正在处理,请等待', { warning: true });
      }

      if (!saveUrl) {
        throw new TipError('no saveUrl');
      }

      // 验证参数
      try {
        const isValid = await formRef.value?.validate();
        if (!isValid) {
          throw new IgnoreError('ValidateFail');
        }
      } catch (e: any) {
        props.invalidFields?.(e);
        throw new IgnoreError('ValidateFail', e);
      }

      // 将返回值复制到model中(去掉不关心的字段)
      let submitData = _.cloneDeep(
        _.pick(toRaw(model), Object.keys(formItems))
      );

      // 拼接objectId
      if (props.objectId?.data) {
        _.defaults(submitData, props.objectId?.data);
      }

      // 处理表格参数, 不必要的字段不需要提交
      for (let [tableName, dataFun] of Object.entries(tableInputs)) {
        submitData[tableName] = dataFun();
      }

      if (hook?.before?.(submitData) === false) {
        throw new IgnoreError('cancel');
      }

      if (wrapKey) {
        submitData = { [wrapKey]: submitData };
      }

      // 执行提交操作
      try {
        loading.value = true;
        const result = await axiosPost(saveUrl, submitData);

        // 保存状态
        for (let item of Object.values(formItems)) {
          if (_.isObject(item)) {
            item.persist?.();
          }
        }

        // 后处理
        hook?.after?.(result);

        return result;
      } catch (e: any) {
        if (props.submitCatch) {
          props.submitCatch(e);
        }

        throw e;
      } finally {
        loading.value = false;
      }
    };

    //
    // eslint-disable-next-line vue/no-setup-props-destructure
    const name = props.name;
    shareData('form', {
      name,

      // 提交
      submit,

      // 提交loading状态
      loading,

      // 重置
      reset: () => {
        formRef.value?.resetFields();
      },

      items: formItems,

      // 表格元素, 由表格注册上来, 用于确定提交哪些参数
      tables: tableInputs,

      // 传给表格
      objectId: props.objectId,

      model: model
    });

    // 共cmdbar使用, cmdbar是可复用的
    shareData('table', {
      name,

      // 包含api, columns描述, 由formTable注册上来
      ctx: protectedReactive(),

      // 由cmdbar注册上来，(暂无用,得要有个占位的)
      cmd: protectedReactive(),

      // 组合列定义
      // field => {subField => title}
      complexLabels:
        protectedReactive<Record<string, Record<string, string>>>(),

      // 组合列: visible列名
      // field => {subField => visible}
      complexVisibles:
        protectedReactive<Record<string, Record<string, boolean>>>(),

      // 暂无用, 为了适配SearchPanel
      setting: protectedReactive()
    });

    if (props.fetchUrl && hasObjectId) {
      const fetchLoading = ElLoading.service({
        lock: true,
        target: formRef.value?.$el,
        background: '#ffffff00'
      });

      axiosRequest(props.fetchUrl, props.fetchMethod, props.objectId?.data)
        .then((data) => {
          if (data) {
            // 不规则表格, 返回的是数组
            if (_.isArray(data)) {
              if (data.length == 1) {
                data = data[0];
              } else {
                console.error(`form '${name}' response invalid data`, data);
              }
            }

            // 增加一个临时key, 用于前端缓存数据
            for (let tbl of Object.keys(tableInputs)) {
              if (_.isArray((data as any)[tbl])) {
                for (let item of (data as any)[tbl]) {
                  item['_'] = {};
                }
              }
            }

            _.assign(model, data);

            // 加载成功
            ctx.emit('loaded');
          }
        })
        .catch(showError)
        .finally(() => {
          fetchLoading.close();

          // 设置较验规则
          rules.value = (props as any).rules;
        });
    } else {
      rules.value = (props as any).rules;
    }

    // 通知popup更新header\footer slot
    usePopupContainer();

    return { formRef, baseAttrs, vModel: model, submitF: submit, rules };
  },

  methods: {
    // 指定字段表单验证
    async validate(...props: string[]): Promise<boolean | undefined> {
      try {
        return await this.formRef?.validateField(props);
      } catch (e: any) {
        throw new IgnoreError('ValidateFail', e);
      }
    },

    submit(hook?: FormSubmitParams) {
      return this.submitF(hook);
    }
  }
});
</script>

<style lang="scss" scoped>
.fm-cont {
  --easy-page-padding-x: var(--ext-easy-page-padding-x, 0);
  --easy-page-padding-y: var(--ext-easy-page-padding-y, 0);

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  padding: 12px 24px 0 24px;
  text-align: left;

  --easy-cmdbar-border-color: #e6e9ef;
  --fi-font-size: 14px;
  --fi-line-height: 34px;
  --fi-prefix-width: 100px;
  --fi-prefix-left-space: 4px;
  --fi-prefix-right-space: 6px;
  --fi-border-radius: 2px;
  --fi-border-color: #ebebeb;
  --fi-border-hover-color: #297ce6;
  --fi-color-primary-light-8: transparent;

  // :deep(.el-form-item__content) {
  //   //min-width: 160px;
  // }

  //:deep(.el-form-item.is-error) {
  //  --fi-border-hover-color: transparent;
  //}

  // 模拟form必填项
  :deep(.require::before) {
    content: '*';
    color: var(--el-color-danger);
    margin-right: 4px;
  }

  :deep(.hflow) {
    display: flex;
    flex-grow: 1;
  }

  :deep(.vflow) {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
}
</style>
